import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { 
  BookOpen, FileText, Shield, Award, Wrench, Users, Lock, 
  Search, ChevronRight, Download, ArrowLeft, Heart, ClipboardList,
  ExternalLink, Menu, X, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const iconMap = {
  Heart: Heart,
  Award: Award,
  Shield: Shield,
  ClipboardList: ClipboardList,
  Wrench: Wrench,
  Users: Users,
  BookOpen: BookOpen,
  FileText: FileText,
};

const StandardsPage = () => {
  const { categorySlug, sectionSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user is logged in as admin (for internal documents)
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  
  // Check admin status on mount and when localStorage changes
  useEffect(() => {
    const checkAdminStatus = () => {
      const adminUser = localStorage.getItem('admin_user') || localStorage.getItem('adminUser');
      const adminLoggedIn = localStorage.getItem('adminLoggedIn');
      // Check both the flag and presence of user data
      const adminStatus = (adminLoggedIn === 'true' || adminLoggedIn === true) && adminUser !== null && adminUser !== 'null' && adminUser !== undefined;
      console.log('Standards page admin check:', { adminLoggedIn, adminUser: adminUser ? 'exists' : 'null', adminStatus });
      return adminStatus;
    };
    
    const adminStatus = checkAdminStatus();
    setIsAdmin(adminStatus);
    setAdminCheckComplete(true);
    
    // Also listen for storage events (cross-tab login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'adminLoggedIn' || e.key === 'adminUser' || e.key === 'admin_user') {
        const newAdminStatus = checkAdminStatus();
        setIsAdmin(newAdminStatus);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchCategories = async (adminStatus) => {
    try {
      const accessLevel = adminStatus ? 'all' : 'public';
      const res = await fetch(`${API_URL}/api/standards/categories?access_level=${accessLevel}`);
      const data = await res.json();
      setCategories(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  // Only fetch categories after admin check is complete
  useEffect(() => {
    if (adminCheckComplete) {
      fetchCategories(isAdmin);
    }
  }, [adminCheckComplete, isAdmin]);

  useEffect(() => {
    if (categorySlug && categories.length > 0) {
      const cat = categories.find(c => c.slug === categorySlug);
      if (cat) {
        setSelectedCategory(cat);
        fetchSections(cat.id);
      }
    } else if (categories.length > 0 && !categorySlug) {
      // Default to first category
      setSelectedCategory(categories[0]);
      fetchSections(categories[0].id);
    }
  }, [categorySlug, categories]);

  useEffect(() => {
    if (sectionSlug && sections.length > 0) {
      const sec = sections.find(s => s.slug === sectionSlug);
      if (sec) {
        setSelectedSection(sec);
      }
    } else if (sections.length > 0 && !sectionSlug) {
      setSelectedSection(sections[0]);
    }
  }, [sectionSlug, sections]);

  const fetchSections = async (categoryId) => {
    try {
      const res = await fetch(`${API_URL}/api/standards/sections?category_id=${categoryId}`);
      const data = await res.json();
      setSections(data);
      if (data.length > 0 && !sectionSlug) {
        setSelectedSection(data[0]);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      try {
        const accessLevel = isAdmin ? 'all' : 'public';
        const res = await fetch(`${API_URL}/api/standards/search?q=${encodeURIComponent(query)}&access_level=${accessLevel}`);
        const data = await res.json();
        setSearchResults(data);
      } catch (error) {
        console.error('Error searching:', error);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedSection(null);
    fetchSections(category.id);
    setSidebarOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setSidebarOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const downloadPdf = async (type, id) => {
    try {
      let url;
      if (type === 'section') {
        url = `${API_URL}/api/standards/pdf/section/${id}`;
      } else if (type === 'category') {
        url = `${API_URL}/api/standards/pdf/category/${id}`;
      } else {
        const accessLevel = isAdmin ? 'all' : 'public';
        url = `${API_URL}/api/standards/pdf/all?access_level=${accessLevel}`;
      }
      
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const CategoryIcon = ({ iconName }) => {
    const Icon = iconMap[iconName] || FileText;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Internal User Banner */}
      {isAdmin && (
        <div className="bg-amber-500 text-amber-900 py-2 px-4 text-center text-sm font-medium mt-[104px]">
          <Lock className="w-4 h-4 inline-block mr-2" />
          You are viewing as an internal user. Internal-only documents are visible.
        </div>
      )}
      
      {/* Hero Section */}
      <section className={`relative bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 ${isAdmin ? 'pt-32' : 'pt-52'} pb-16`}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Standards of Excellence
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Explore our comprehensive policies, procedures, and quality standards that guide everything we do at C3 Contracting.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search standards..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-12 pr-4 py-3 w-full bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/20"
              />
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute left-1/2 -translate-x-1/2 w-full max-w-xl mt-2 bg-white rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSelectedCategory(result.category);
                      fetchSections(result.category.id);
                      setTimeout(() => setSelectedSection(result.section), 100);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                  >
                    <div className="font-medium text-slate-900">{result.section.title}</div>
                    <div className="text-sm text-slate-500">{result.category.name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Mobile Category Navigation Button - More Prominent */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Menu className="w-5 h-5 text-cyan-600" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-slate-900">
                    {selectedCategory ? selectedCategory.name : 'Browse Categories'}
                  </div>
                  <div className="text-sm text-slate-500">
                    {categories.length} categories • Tap to navigate
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="flex gap-8">
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
              <div 
                className="lg:hidden fixed inset-0 bg-black/50 z-20"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            {/* Sidebar */}
            <aside className={`
              fixed lg:static inset-y-0 left-0 z-30 w-80 bg-white lg:bg-transparent
              transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
              transition-transform duration-300 ease-in-out
              lg:w-72 shrink-0 overflow-y-auto
              pt-20 lg:pt-0
            `}>
              {/* Mobile Sidebar Header */}
              <div className="lg:hidden sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
                <h3 className="font-bold text-lg text-slate-900">Categories</h3>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 lg:sticky lg:top-24 m-4 lg:m-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 hidden lg:block">Categories</h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => downloadPdf('all')}
                    className="text-cyan-600 hover:text-cyan-700"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download All
                  </Button>
                </div>
                
                <nav className="space-y-1">
                  {categories.map((category) => (
                    <div key={category.id}>
                      <button
                        onClick={() => handleCategorySelect(category)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedCategory?.id === category.id
                            ? 'bg-cyan-50 text-cyan-700'
                            : 'hover:bg-slate-50 text-slate-600'
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg ${
                          selectedCategory?.id === category.id
                            ? 'bg-cyan-100 text-cyan-600'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          <CategoryIcon iconName={category.icon} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{category.name}</div>
                          <div className="text-xs text-slate-400">
                            {category.section_count} {category.section_count === 1 ? 'document' : 'documents'}
                          </div>
                        </div>
                        {category.access_level === 'internal' && (
                          <Lock className="w-4 h-4 text-amber-500" />
                        )}
                      </button>
                      
                      {/* Sections under selected category */}
                      {selectedCategory?.id === category.id && sections.length > 0 && (
                        <div className="ml-6 mt-1 border-l-2 border-slate-200 pl-3 space-y-1">
                          {sections.map((section) => (
                            <button
                              key={section.id}
                              onClick={() => handleSectionSelect(section)}
                              className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                                selectedSection?.id === section.id
                                  ? 'bg-cyan-100 text-cyan-700 font-medium'
                                  : 'text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              {section.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>

                {isAdmin && (
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <Link to="/admin/standards">
                      <Button variant="outline" className="w-full">
                        <FileText className="w-4 h-4 mr-2" />
                        Manage Standards
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 min-w-0">
              {selectedSection ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                  {/* Document Header */}
                  <div className="border-b border-slate-200 p-6">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4">
                      <Link to="/standards" className="hover:text-cyan-600">Standards</Link>
                      <ChevronRight className="w-4 h-4" />
                      <span>{selectedCategory?.name}</span>
                      <ChevronRight className="w-4 h-4" />
                      <span className="text-slate-900 font-medium">{selectedSection.title}</span>
                    </div>
                    
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                          {selectedSection.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <span>Version {selectedSection.version}</span>
                          <span>•</span>
                          <span>Last updated {new Date(selectedSection.last_revised).toLocaleDateString()}</span>
                          {selectedCategory?.access_level === 'internal' && (
                            <>
                              <span>•</span>
                              <span className="inline-flex items-center gap-1 text-amber-600">
                                <Lock className="w-3 h-3" />
                                Internal Only
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline"
                        onClick={() => downloadPdf('section', selectedSection.id)}
                        className="shrink-0"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  
                  {/* Document Content */}
                  <div className="p-6 md:p-8">
                    <article className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-a:text-cyan-600 prose-strong:text-slate-900 prose-table:text-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {selectedSection.content}
                      </ReactMarkdown>
                    </article>
                  </div>

                  {/* Document Footer */}
                  <div className="border-t border-slate-200 p-6 bg-slate-50 rounded-b-xl">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="text-sm text-slate-500">
                        <strong>Document ID:</strong> {selectedSection.id.slice(0, 8)}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPdf('category', selectedCategory?.id)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download Category
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Category Overview (when no section is selected)
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                  {selectedCategory ? (
                    <>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-cyan-100 rounded-xl text-cyan-600">
                          <CategoryIcon iconName={selectedCategory.icon} />
                        </div>
                        <div>
                          <h1 className="text-2xl font-bold text-slate-900">{selectedCategory.name}</h1>
                          <p className="text-slate-500">{selectedCategory.description}</p>
                        </div>
                      </div>
                      
                      <div className="grid gap-4">
                        {sections.map((section) => (
                          <button
                            key={section.id}
                            onClick={() => handleSectionSelect(section)}
                            className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                          >
                            <div>
                              <h3 className="font-medium text-slate-900">{section.title}</h3>
                              <p className="text-sm text-slate-500">
                                Version {section.version} • Updated {new Date(section.last_revised).toLocaleDateString()}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h2 className="text-xl font-bold text-slate-900 mb-2">Select a Category</h2>
                      <p className="text-slate-500">Choose a category from the sidebar to view standards documents.</p>
                    </div>
                  )}
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StandardsPage;
